import React from 'react';
import { Card, Table, Badge, Button } from '../ui';
import { Filter, RefreshCw, XCircle, ArrowRight } from 'lucide-react';
export function TransactionInvestigationView() {
  const transactions = [{
    id: 'TXN-88293-001',
    type: 'P2P Transfer',
    flow: {
      from: 'RBC',
      to: 'BOB'
    },
    amount: '$150.00',
    status: 'Success',
    duration: '120ms'
  }, {
    id: 'TXN-88293-002',
    type: 'Bill Payment',
    flow: {
      from: 'Scotiabank',
      to: 'BPL'
    },
    amount: '$45.50',
    status: 'Success',
    duration: '145ms'
  }, {
    id: 'TXN-88293-003',
    type: 'P2P Transfer',
    flow: {
      from: 'FCI',
      to: 'RBC'
    },
    amount: '$2500.00',
    status: 'Error',
    duration: '9002ms'
  }, {
    id: 'TXN-88293-004',
    type: 'Merchant Pay',
    flow: {
      from: 'BOB',
      to: 'SuperValu'
    },
    amount: '$12.99',
    status: 'Success',
    duration: '110ms'
  }, {
    id: 'TXN-88293-005',
    type: 'Gov Payout',
    flow: {
      from: 'Central Bank',
      to: 'FCI'
    },
    amount: '$450.00',
    status: 'Success',
    duration: '130ms'
  }];
  const bpmnSteps = [{
    title: 'Step 1: Parsing Incoming Message',
    desc: 'ISO 20022 payload validated (12ms)'
  }, {
    title: 'Step 2: Access Token Verification',
    desc: 'OAuth2 token verified (45ms)'
  }, {
    title: 'Step 3: Liquidity Check',
    desc: 'Sufficient funds confirmed (8ms)'
  }, {
    title: 'Step 4: Core Settlement',
    desc: 'Ledger updated successfully (55ms)'
  }, {
    title: 'Step 5: Response Callback',
    desc: 'HTTP 200 sent to originator (0ms)'
  }];
  return <div className="space-y-6" data-id="element-670">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-671">
        <div data-id="element-672">
          <h2 className="text-3xl font-serif font-bold text-bank-navy" data-id="element-673">
            Transaction & Error Investigation
          </h2>
          <p className="text-lg text-slate-500 mt-2" data-id="element-674">
            Audit logs, deep-dive analysis, and BPMN process tracing.
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-675">
          <div className="flex items-center space-x-2 bg-white border border-slate-300 rounded-md px-3 py-2" data-id="element-676">
            <span className="text-sm text-slate-600" data-id="element-677">
              Transaction Store Level:
            </span>
            <select className="text-sm font-medium text-bank-navy bg-transparent border-none focus:ring-0 cursor-pointer" data-id="element-678">
              <option data-id="element-679">Extended</option>
              <option data-id="element-680">Basic</option>
              <option data-id="element-681">Full Trace</option>
            </select>
          </div>
          <Button variant="outline" leftIcon={<Filter className="h-4 w-4" data-id="element-683" />} data-id="element-682">
            Filters
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-684">
        {/* Left Column: Log */}
        <div className="lg:col-span-3" data-id="element-685">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-686">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white" data-id="element-687">
              <h3 className="text-lg font-serif font-bold text-bank-navy" data-id="element-688">
                Live Transaction Log
              </h3>
              <div className="flex space-x-2" data-id="element-689">
                <input type="text" placeholder="Filter by ID..." className="w-48 border border-slate-300 rounded-md text-sm px-3 py-1.5 focus:outline-none focus:border-bank-navy focus:ring-1 focus:ring-bank-navy" data-id="element-690" />
                <Button variant="outline" size="sm" leftIcon={<RefreshCw className="h-3 w-3" data-id="element-692" />} data-id="element-691">
                  Refresh
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-693">
              <Table data={transactions} columns={[{
              header: 'TRANSACTION ID',
              accessorKey: 'id' as const,
              className: 'font-mono text-sm text-slate-600'
            }, {
              header: 'TYPE',
              accessorKey: 'type' as const,
              className: 'text-sm text-slate-900'
            }, {
              header: 'FLOW',
              className: 'text-sm text-slate-900',
              cell: (item: any) => <div className="flex items-center space-x-1 font-medium" data-id="element-695">
                        <span data-id="element-696">{item.flow.from}</span>
                        <ArrowRight className="h-3 w-3 text-slate-400" data-id="element-697" />
                        <span data-id="element-698">{item.flow.to}</span>
                      </div>
            }, {
              header: 'AMOUNT',
              accessorKey: 'amount' as const,
              className: 'font-mono text-sm text-slate-900'
            }, {
              header: 'STATUS',
              accessorKey: 'status' as const,
              cell: (item: any) => <Badge variant={item.status === 'Success' ? 'success' : 'error'} className="flex items-center w-fit" data-id="element-699">
                        {item.status === 'Error' && <XCircle className="h-3 w-3 mr-1" data-id="element-700" />}
                        {item.status}
                      </Badge>
            }, {
              header: 'DURATION',
              accessorKey: 'duration' as const,
              className: 'text-sm text-slate-500'
            }]} data-id="element-694" />
            </div>
          </div>
        </div>

        {/* Right Column: Details */}
        <div className="lg:col-span-2" data-id="element-701">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm sticky top-6" data-id="element-702">
            <div className="px-6 py-5 border-b border-slate-100" data-id="element-703">
              <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-704">
                Transaction Details
              </h3>
            </div>
            <div className="p-6 space-y-8" data-id="element-705">
              {/* Transaction Info */}
              <div data-id="element-706">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-707">
                  TRANSACTION ID
                </p>
                <p className="font-mono text-xl font-bold text-bank-navy mb-3" data-id="element-708">
                  TXN-88293-001
                </p>
                <Badge variant="success" className="mb-4" data-id="element-709">
                  Success
                </Badge>
                <div className="space-y-1 text-sm text-slate-600" data-id="element-710">
                  <p data-id="element-711">
                    <span className="font-medium text-slate-700" data-id="element-712">Type:</span>{' '}
                    P2P Transfer
                  </p>
                  <p data-id="element-713">
                    <span className="font-medium text-slate-700" data-id="element-714">
                      Total Duration:
                    </span>{' '}
                    120ms
                  </p>
                  <p data-id="element-715">
                    <span className="font-medium text-slate-700" data-id="element-716">
                      Store Level:
                    </span>{' '}
                    Extended
                  </p>
                </div>
              </div>

              {/* BPMN Steps */}
              <div data-id="element-717">
                <h4 className="font-serif font-bold text-bank-navy text-base mb-4" data-id="element-718">
                  Investigation Step List (BPMN Path)
                </h4>
                <div className="relative space-y-6 pl-2" data-id="element-719">
                  {/* Vertical Line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200" data-id="element-720" />

                  {bpmnSteps.map((step, idx) => <div key={idx} className="relative flex items-start" data-id="element-721">
                      <div className="absolute left-0 mt-1.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-4 ring-white z-10" data-id="element-722" />
                      <div className="ml-6" data-id="element-723">
                        <p className="text-sm font-bold text-bank-navy" data-id="element-724">
                          {step.title}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-725">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Action Button */}
              <Button variant="outline" className="w-full mt-2" data-id="element-726">
                Download Full Trace Log
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}